import { createMemo, createSignal, Signal, For, createEffect } from 'solid-js';
import { Show } from 'solid-js';
import { SearchCriteria, SearchCriteriaElement, SearchCriteriaGridElement, requireFindResultPanelContext } from '../FindResultPanelContext';
import { Button, Dropdown, Icon, Input } from '../../..';
import STToolButton from '../../st-tool-button/st-tool-button';
import styles from '../st-find-result-panel.module.css';

export function SearchCriteriaList(params: {
    searchCriteria: Signal<SearchCriteria>;
    onDeleteSearchCriteria?: (srcName: string) => void;
}) {
    const context = requireFindResultPanelContext();
    const [searchCriteria, setSearchCriteria] = params.searchCriteria;
    const [isCollapsed, setIsCollapsed] = createSignal(true);

    function getItems(controls: Record<string, SearchCriteriaElement>) {
        const res: Array<SearchCriteriaElement> = [];

        if (controls) {
            for (const [key, value] of Object.entries(controls)) {
                if (!value.isData) {
                    res.push({
                        srcName: key,
                        ...(value as any),
                    });
                }
            }
        }

        return res;
    }

    const grids = createMemo(() => {
        const sc = searchCriteria();
        return Object.entries(sc.grids || {})
            .map(([key, value]) => {
                return {
                    guid: key,
                    ...(value as any),
                };
            });
    });

    const removeSearchCriteria = (it: SearchCriteriaElement) => {
        const sc = { ...searchCriteria() };

        delete sc.controls[it.srcName];
        setSearchCriteria(sc);

        if (params.onDeleteSearchCriteria) {
            params.onDeleteSearchCriteria(it.srcName);
        }
    };

    const removeGridSearchCriteria = (grid: SearchCriteriaGridElement, it: SearchCriteriaElement) => {
        const sc = { ...searchCriteria() };

        delete sc.grids[grid.guid].columns[it.srcName];
        if (Object.keys(sc.grids[grid.guid].columns).length === 0) {
            delete sc.grids[grid.guid];
        }

        setSearchCriteria(sc);

        if (params.onDeleteSearchCriteria) {
            params.onDeleteSearchCriteria(it.srcName);
        }
    };

    const getDisplayValues = (it: SearchCriteriaElement) => {
        const value = it.desc || it.value;
        const res = !!value
            ? value.split(',')
            : [];

        if (it.condition?.values) {
            res.push.apply(res, it.condition.values);
        }

        return res.join(', ');
    };

    const [filterName, setFilterName] = createSignal('');
    const [showCreateFilter, setShowCreateFilter] = createSignal(false);
    async function createFilter() {
        window.ST.SendServerEvent(null, 'Rev_Create_Filter', {
            '@FILTER_NAME': filterName(),
            '@ACTIVATE': 'Y',
            'VIEW_STATE': JSON.stringify(context.viewState())
        });

        setShowCreateFilter(false);
        setFilterName('');
    }

    function initFilterEvents(elFilter: HTMLInputElement) {
        createEffect(() => {
            if (showCreateFilter()) {
                requestAnimationFrame(() => elFilter.focus());
            }
        });

        elFilter.addEventListener('keypress', (ev: KeyboardEvent) => {
            if (ev.key === 'Enter') {
                createFilter();
                setShowCreateFilter(false);
            }
        });
    }

    return <Show when={Object.keys(searchCriteria() || {}).length > 0}>
        <div class="search-criteria" classList={{ collapsed: isCollapsed() }}>
            <h3 class="flexbox horizontal middle" onClick={() => setIsCollapsed(!isCollapsed())}>
                <span>
                    {isCollapsed()
                        ? <i class="fas fa-caret-right"></i>
                        : <i class="fas fa-caret-down"></i>}
                </span>
                <span class="auto">Search Criteria</span>

                <span onClick={(ev) => { ev.stopPropagation(); }}>
                    <Dropdown placement="bottom-end" open={[showCreateFilter, setShowCreateFilter]}>
                        <Dropdown.Trigger>
                            <span use:tooltip="Create Filter">
                                <Icon src="far fa-filter" aria-label="Create Filter" />
                            </span>
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                            <div class="flexbox vertical padded" style="min-width: 180px">
                                <h2 class={styles.title}>New Filter</h2>
                                <div>
                                    <Input focused defaultInput={createFilter} value={[filterName, setFilterName]} placeholder="Filter Name" />
                                </div>
                                <div>
                                    <Button type="primary" onClick={createFilter}>Create</Button>
                                </div>
                            </div>
                        </Dropdown.Content>
                    </Dropdown>
                </span>
            </h3>
            <div>
                <For each={getItems(searchCriteria().controls)}>{(it) =>
                    <div class="flexbox horizontal" style="margin: 2px; padding: 3px; background-color: var(--st-color-search-item-bg)">
                        <div class="auto">
                            <span style="color:#888">{it.label}</span>:
                            <em class={styles['search-item-value']}><span>{getDisplayValues(it)}</span></em>
                        </div>
                        <div class="item-tools">
                            <STToolButton icon="fas fa-times" label="Remove" onClick={() => removeSearchCriteria(it)} />
                        </div>
                    </div>
                }</For>
                <For each={grids()}>{(grid) =>
                    <div class="flexbox vertical" style="margin: 2px; padding: 3px; background-color: var(--st-color-search-item-bg)">
                        <span style="color:#888">{grid.label}</span>
                        <For each={getItems(grid.columns)}>{(it) =>
                            <div class="flexbox horizontal" style="margin-left: 50px">
                                <div class="auto">
                                    <span style="color:#888">{it.label}</span>:
                                    <em class={styles['search-item-value']}><span>{getDisplayValues(it)}</span></em>
                                </div>
                                <div class="item-tools">
                                    <STToolButton icon="fas fa-times" label="Remove" onClick={() => removeGridSearchCriteria(grid, it)} />
                                </div>
                            </div>
                        }</For>
                    </div>
                }</For>
            </div>
        </div>
    </Show>
}
